import { useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useTranslation } from '@/src/i18n';
import { deleteAccountUseCase } from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';
import { platformAlert } from '@/src/core/utils/platformAlert';

export default function DeleteAccountPage() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user, signIn, signOut } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const handleSignIn = async () => {
    if (!email.trim() || !password.trim()) {
      platformAlert(t('common.error'), t('alerts.enterEmailPassword'));
      return;
    }
    try {
      setSigningIn(true);
      const { error } = await signIn(email.trim(), password);
      if (error) {
        platformAlert(t('auth.loginFailed'), error.message || t('auth.invalidCredentials'));
      }
    } catch (error: any) {
      platformAlert(t('common.error'), error.message || t('errors.somethingWentWrong'));
    } finally {
      setSigningIn(false);
    }
  };

  const handleDeleteAccount = () => {
    platformAlert(
      t('settings.deleteAccount'),
      t('settings.deleteAccountWarning'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('settings.deleteAccountConfirm'),
          style: 'destructive',
          onPress: async () => {
            if (!user) return;
            setDeleting(true);
            try {
              const avatarUrl = user.user_metadata?.avatar_url as string | undefined;
              await deleteAccountUseCase.execute(user.id, avatarUrl);
              await signOut();
              setDeleted(true);
            } catch (error) {
              logger.error('DeleteAccountPage', 'Failed to delete account', { error });
              platformAlert(t('common.error'), t('errors.deleteAccountFailed'));
            } finally {
              setDeleting(false);
            }
          },
        },
      ]
    );
  };

  if (deleted) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <Ionicons name="checkmark-circle" size={64} color={colors.success} />
        <Text style={[styles.successTitle, { color: colors.text }]}>{t('deleteAccountPage.successTitle')}</Text>
        <Text style={[styles.successMessage, { color: colors.textSecondary }]}>{t('deleteAccountPage.successMessage')}</Text>
        <TouchableOpacity
          style={[styles.button, { backgroundColor: colors.primary }]}
          onPress={() => router.replace('/')}
        >
          <Text style={[styles.buttonText, { color: colors.primaryText }]}>{t('deleteAccountPage.backToApp')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={{ backgroundColor: colors.background }} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="trash-outline" size={48} color={colors.error} />
        <Text style={[styles.title, { color: colors.text }]}>{t('deleteAccountPage.title')}</Text>
      </View>

      {/* Info card */}
      <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <Text style={[styles.introText, { color: colors.textSecondary }]}>{t('deleteAccountPage.intro')}</Text>
        <View style={styles.bullets}>
          {(['bullet1', 'bullet2', 'bullet3', 'bullet4'] as const).map((key) => (
            <View key={key} style={styles.bulletItem}>
              <Ionicons name="remove-circle" size={14} color={colors.error} style={styles.bulletDot} />
              <Text style={[styles.bulletText, { color: colors.textSecondary }]}>{t(`deleteAccountPage.${key}`)}</Text>
            </View>
          ))}
        </View>
        <View style={[styles.warningBox, { backgroundColor: colors.error + '18', borderColor: colors.error + '50' }]}>
          <Ionicons name="warning-outline" size={16} color={colors.error} />
          <Text style={[styles.warningText, { color: colors.error }]}>{t('deleteAccountPage.warning')}</Text>
        </View>
      </View>

      {/* Login form or delete button */}
      {!user ? (
        <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('deleteAccountPage.signInToDelete')}</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.background, borderColor: colors.border, color: colors.text }]}
            placeholder={t('auth.email')}
            placeholderTextColor={colors.textTertiary}
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <TextInput
            style={[styles.input, { backgroundColor: colors.background, borderColor: colors.border, color: colors.text }]}
            placeholder={t('auth.password')}
            placeholderTextColor={colors.textTertiary}
            value={password}
            onChangeText={setPassword}
            secureTextEntry
          />
          <TouchableOpacity
            style={[styles.button, { backgroundColor: colors.error, opacity: signingIn ? 0.7 : 1 }]}
            onPress={handleSignIn}
            disabled={signingIn}
          >
            {signingIn ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.buttonText}>{t('deleteAccountPage.signInAndDelete')}</Text>
            )}
          </TouchableOpacity>
        </View>
      ) : (
        <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Text style={[styles.signedInAs, { color: colors.textSecondary }]}>
            {t('deleteAccountPage.signedInAs')}{' '}
            <Text style={{ color: colors.text, fontWeight: '600' }}>{user.email}</Text>
          </Text>
          <TouchableOpacity
            style={[styles.button, { backgroundColor: colors.error, opacity: deleting ? 0.7 : 1 }]}
            onPress={handleDeleteAccount}
            disabled={deleting}
          >
            {deleting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="trash-outline" size={18} color="#fff" style={{ marginRight: 8 }} />
                <Text style={styles.buttonText}>{t('settings.deleteAccount')}</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      )}

      <TouchableOpacity onPress={() => router.replace('/')} style={styles.backLink}>
        <Text style={[styles.backLinkText, { color: colors.primary }]}>{t('deleteAccountPage.backToApp')}</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 16,
  },
  content: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 560,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 12,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
    marginBottom: 16,
    gap: 12,
  },
  introText: {
    fontSize: 15,
    lineHeight: 22,
  },
  bullets: {
    gap: 8,
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  bulletDot: {
    marginTop: 1,
  },
  bulletText: {
    fontSize: 14,
    flex: 1,
    lineHeight: 20,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginTop: 4,
  },
  warningText: {
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
    lineHeight: 18,
  },
  sectionTitle: {
    fontSize: 15,
    lineHeight: 22,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
  },
  button: {
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    marginTop: 4,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  signedInAs: {
    fontSize: 14,
    textAlign: 'center',
  },
  backLink: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  backLinkText: {
    fontSize: 15,
    fontWeight: '500',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
  },
});
